import React, { useState, useEffect } from 'react';
import { getDailySummary, getTopProducts } from '../../lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { 
  Loader2, TrendingUp, Euro, Receipt, AlertTriangle
} from 'lucide-react';
import { format } from 'date-fns';
import { de } from 'date-fns/locale';
import { extractStornoReason } from '../orderMeta';

export default function PosReports() {
  const [summary, setSummary] = useState(null);
  const [topProducts, setTopProducts] = useState([]);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    setIsLoading(true);
    try {
      const [summaryData, products] = await Promise.all([
        getDailySummary(),
        getTopProducts(null, 10)
      ]);
      setSummary(summaryData);
      setTopProducts(products);
    } catch (error) {
      console.error('Error loading data:', error);
    } finally {
      setIsLoading(false);
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-full">
        <Loader2 className="w-8 h-8 animate-spin text-[#1a2f38]" />
      </div>
    );
  }

  return (
    <div className="p-4 space-y-4" data-testid="pos-reports">
      <div>
        <h1 className="text-xl font-bold text-[#1a2f38]">Tagesbericht</h1>
        <p className="text-sm text-muted-foreground">
          {format(new Date(), 'dd. MMMM yyyy', { locale: de })}
        </p>
      </div>

      {/* Quick Stats */}
      <div className="grid grid-cols-2 gap-3">
        <Card className="shadow-sm border-l-4 border-l-green-500">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <Euro className="w-8 h-8 text-green-600" />
              <div>
                <p className="text-sm text-muted-foreground">Umsatz</p>
                <p className="text-2xl font-bold text-green-700">
                  €{summary?.total_sales?.toFixed(2) || '0.00'}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="shadow-sm border-l-4 border-l-blue-500">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <Receipt className="w-8 h-8 text-blue-600" />
              <div>
                <p className="text-sm text-muted-foreground">Bestellungen</p>
                <p className="text-2xl font-bold text-blue-700">
                  {summary?.total_orders || 0}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="shadow-sm border-l-4 border-l-red-500">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <AlertTriangle className="w-8 h-8 text-red-600" />
              <div>
                <p className="text-sm text-muted-foreground">Stornos</p>
                <p className="text-2xl font-bold text-red-700">
                  {summary?.cancellation_count || 0}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="shadow-sm border-l-4 border-l-slate-500">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <Euro className="w-8 h-8 text-muted-foreground" />
              <div>
                <p className="text-sm text-muted-foreground">Netto nach Storno</p>
                <p className="text-2xl font-bold text-foreground">
                  €{summary?.sales_after_cancellations?.toFixed(2) || '0.00'}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Payment Breakdown */}
      <Card className="shadow-sm">
        <CardHeader className="pb-2">
          <CardTitle className="text-base">Zahlungsarten</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 gap-4">
            <div className="p-3 bg-green-50 rounded-lg">
              <p className="text-sm text-green-700">Bar</p>
              <p className="text-xl font-bold text-green-800">
                €{summary?.cash_total?.toFixed(2) || '0.00'}
              </p>
            </div>
            <div className="p-3 bg-blue-50 rounded-lg">
              <p className="text-sm text-blue-700">Karte</p>
              <p className="text-xl font-bold text-blue-800">
                €{summary?.card_total?.toFixed(2) || '0.00'}
              </p>
            </div>
          </div>
        </CardContent>
      </Card>

      <Card className="shadow-sm">
        <CardHeader className="pb-2">
          <CardTitle className="text-base flex items-center gap-2">
            <AlertTriangle className="w-4 h-4 text-red-500" />
            Storno-Dokumentation
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="mb-4 rounded-lg bg-red-50 p-3">
            <p className="text-sm text-red-700">Storno-Summe</p>
            <p className="text-2xl font-bold text-red-800">
              €{summary?.cancellation_amount?.toFixed(2) || '0.00'}
            </p>
          </div>

          {(summary?.cancelled_orders || []).length === 0 ? (
            <p className="text-center py-2 text-muted-foreground">Keine Stornos heute</p>
          ) : (
            <div className="space-y-2">
              {summary.cancelled_orders.slice(0, 5).map((order) => (
                <div key={order.id} className="rounded-lg border px-3 py-2">
                  <div className="flex items-center justify-between gap-3">
                    <div>
                      <p className="font-semibold text-sm text-foreground">
                        #{order.order_number?.split('-')[1] ?? order.order_number}
                      </p>
                      <p className="text-xs text-muted-foreground">
                        {order.cancelled_at ? format(new Date(order.cancelled_at), 'HH:mm', { locale: de }) : '–'}
                      </p>
                    </div>
                    <p className="font-bold text-red-700">€{order.total?.toFixed(2)}</p>
                  </div>
                  <p className="mt-2 text-xs text-muted-foreground">
                    {extractStornoReason(order.notes) || order.notes || 'Kein Grund hinterlegt.'}
                  </p>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Top Products */}
      <Card className="shadow-sm">
        <CardHeader className="pb-2">
          <CardTitle className="text-base flex items-center gap-2">
            <TrendingUp className="w-4 h-4" />
            Top Artikel
          </CardTitle>
        </CardHeader>
        <CardContent>
          {topProducts.length === 0 ? (
            <p className="text-center py-4 text-muted-foreground">Keine Verkäufe</p>
          ) : (
            <div className="space-y-2">
              {topProducts.map((product, index) => (
                <div key={product.product_id} className="flex items-center justify-between py-2 border-b last:border-0">
                  <div className="flex items-center gap-2">
                    <span className="w-6 h-6 rounded-full bg-muted flex items-center justify-center text-sm font-bold">
                      {index + 1}
                    </span>
                    <span className="font-medium text-sm">{product.product_name}</span>
                  </div>
                  <div className="text-right">
                    <p className="font-bold tabular-nums">€{product.total?.toFixed(2)}</p>
                    <p className="text-xs text-muted-foreground">{product.quantity}x</p>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
